// src/components/admin/PromoteStudents.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { mbbsAPI, internAPI } from '../../services/api';
import { notify, confirmDialog } from '../../utils/notify';

/* ─── constants ──────────────────────────────────────────────────────────── */

const YEAR_CONFIG = {
  1: { label: '1st Year', color: '#2563eb', bg: '#dbeafe', subjects: 'Anatomy · Physiology · Biochemistry' },
  2: { label: '2nd Year', color: '#059669', bg: '#d1fae5', subjects: 'Microbiology · Pathology · Pharmacology' },
  3: { label: '3rd Year', color: '#d97706', bg: '#fef3c7', subjects: 'Forensic Medicine · Community Medicine' },
  4: { label: '4th Year', color: '#7c3aed', bg: '#ede9fe', subjects: 'Medicine · Surgery · ObGyn · Paediatrics · ENT · Ophthalmology' },
};

const ALL_YEARS = [1, 2, 3, 4];

/* ─── sub-components ─────────────────────────────────────────────────────── */

function YearBadge({ year, size = 'md' }) {
  const cfg = YEAR_CONFIG[year] || YEAR_CONFIG[1];
  const padding = size === 'sm' ? '2px 7px' : '3px 10px';
  const fontSize = size === 'sm' ? 11 : 12;
  return (
    <span style={{
      display: 'inline-block',
      padding,
      borderRadius: 20,
      background: cfg.bg,
      color: cfg.color,
      fontWeight: 700,
      fontSize,
      whiteSpace: 'nowrap',
    }}>
      {cfg.label}
    </span>
  );
}

function DeptChip({ dept }) {
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 7px',
      borderRadius: 6,
      background: '#f1f5f9',
      color: '#475569',
      fontSize: 11,
      fontWeight: 500,
      border: '1px solid #e2e8f0',
    }}>
      {dept.code}
    </span>
  );
}

function ResultModal({ result, onClose }) {
  if (!result) return null;

  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={S.resultModal} onClick={e => e.stopPropagation()}>
        <div style={S.resultHeader}>
          <h3 style={S.resultTitle}>Promotion Complete</h3>
          <button style={S.closeBtn} onClick={onClose}>×</button>
        </div>

        {/* Summary chips */}
        <div style={S.resultSummary}>
          <div style={{ ...S.resultChip, background: '#dcfce7', color: '#166534' }}>
            <span style={S.resultChipNum}>{result.promoted_count}</span>
            <span style={S.resultChipLabel}>Promoted</span>
          </div>
          {result.skipped_count > 0 && (
            <div style={{ ...S.resultChip, background: '#fef9c3', color: '#854d0e' }}>
              <span style={S.resultChipNum}>{result.skipped_count}</span>
              <span style={S.resultChipLabel}>Skipped (Max Year)</span>
            </div>
          )}
          {result.not_found_count > 0 && (
            <div style={{ ...S.resultChip, background: '#fee2e2', color: '#991b1b' }}>
              <span style={S.resultChipNum}>{result.not_found_count}</span>
              <span style={S.resultChipLabel}>Not Found</span>
            </div>
          )}
        </div>

        {/* Promoted list */}
        {result.promoted?.length > 0 && (
          <div style={S.resultSection}>
            <h4 style={S.resultSectionTitle}>✓ Successfully Promoted</h4>
            <div style={S.resultList}>
              {result.promoted.map((s, i) => (
                <div key={i} style={S.resultRow}>
                  <span style={S.resultName}>{s.name}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <YearBadge year={s.from_year} size="sm" />
                    <span style={{ color: '#94a3b8', fontSize: 11 }}>→</span>
                    <YearBadge year={s.to_year} size="sm" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Already max year */}
        {result.already_max_year?.length > 0 && (
          <div style={S.resultSection}>
            <h4 style={{ ...S.resultSectionTitle, color: '#854d0e' }}>
              ⚠ Already at 4th Year (Skipped)
            </h4>
            <div style={S.resultList}>
              {result.already_max_year.map((s, i) => (
                <div key={i} style={S.resultRow}>
                  <span style={S.resultName}>{s.name}</span>
                  <YearBadge year={s.year} size="sm" />
                </div>
              ))}
            </div>
          </div>
        )}

        <button style={S.doneBtn} onClick={onClose}>Close</button>
      </div>
    </div>
  );
}

/* ─── Intern Promotion Panel ─────────────────────────────────────────────── */

function InternPromotionPanel() {
  const [year4Students,   setYear4Students]   = useState([]);
  const [existingInterns, setExistingInterns] = useState([]);
  const [selected,        setSelected]        = useState(new Set());
  const [notes,           setNotes]           = useState('');
  const [loading,         setLoading]         = useState(true);
  const [promoting,       setPromoting]       = useState(false);
  const [result,          setResult]          = useState(null);
  const [error,           setError]           = useState('');
  const [internFilter,    setInternFilter]    = useState('ACTIVE');

  const load = async () => {
    setLoading(true); setError('');
    try {
      const [stuData, internData] = await Promise.all([
        mbbsAPI.getStudents({ year: 4 }),
        internAPI.getAll({ status: internFilter }),
      ]);
      const stuList    = Array.isArray(stuData)    ? stuData    : (stuData.results    || []);
      const existList  = Array.isArray(internData) ? internData : (internData.results || []);
      const internUserIds = new Set(existList.map(i => i.user));
      setYear4Students(stuList.filter(s => !internUserIds.has(s.id)));
      setExistingInterns(existList);
    } catch {
      setError('Failed to load student data. Please refresh.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [internFilter]);

  const toggleSelect = (profileId) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(profileId) ? next.delete(profileId) : next.add(profileId);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === year4Students.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(year4Students.map(s => s.profile_id)));
    }
  };

  const handlePromote = async () => {
    if (selected.size === 0) return;
    if (!await confirmDialog(
      `Promote ${selected.size} student${selected.size !== 1 ? 's' : ''} to 1-year Internship? This changes their role to INTERN.`
    )) return;

    setPromoting(true); setError(''); setResult(null);
    try {
      const res = await internAPI.promoteToIntern(Array.from(selected), notes);
      setResult(res);
      setSelected(new Set());
      setNotes('');
      load();
    } catch (e) {
      setError(
        e.response?.data?.error ||
        e.response?.data?.detail ||
        'Promotion failed. Please try again.'
      );
    } finally {
      setPromoting(false); }
  };

  const fmtDate = s =>
    s
      ? new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
      : '—';

  return (
    <div style={{ fontFamily: "'DM Sans','Segoe UI',sans-serif", display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* Result banner */}
      {result && (
        <div style={{
          background: result.promoted_count > 0 ? '#f0fdf4' : '#fef9c3',
          border: `1px solid ${result.promoted_count > 0 ? '#86efac' : '#fde68a'}`,
          borderRadius: 10,
          padding: '12px 16px',
          fontSize: 13,
        }}>
          {result.promoted_count > 0 && (
            <div style={{ color: '#166534', fontWeight: 700, marginBottom: result.errors?.length > 0 ? 6 : 0 }}>
              ✓ {result.promoted_count} student{result.promoted_count !== 1 ? 's' : ''} promoted to Internship.
            </div>
          )}
          {result.promoted?.map(p => (
            <div key={p.profile_id} style={{ fontSize: 12, color: '#166534', marginTop: 2 }}>
              → {p.name} · Stipend: ₹{Number(p.monthly_salary).toLocaleString('en-IN')}/mo · Starts: {p.start_date}
            </div>
          ))}
          {result.errors?.length > 0 && (
            <div style={{ color: '#854d0e', marginTop: 6 }}>
              ⚠ {result.errors.length} skipped: {result.errors.map(e => `${e.profile_id} (${e.reason})`).join(', ')}
            </div>
          )}
          <button
            onClick={() => setResult(null)}
            style={{ marginTop: 8, background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 12 }}
          >
            Dismiss
          </button>
        </div>
      )}

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', padding: '10px 14px', borderRadius: 8, fontSize: 13 }}>
          {error}
        </div>
      )}

      {/* ── Year-4 students eligible for intern promotion ── */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, overflow: 'hidden' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 800, color: '#0f172a' }}>4th-Year Students — Eligible for Internship</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
              {loading ? 'Loading…' : `${year4Students.length} student${year4Students.length !== 1 ? 's' : ''} available · ${selected.size} selected`}
            </div>
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 36, color: '#64748b', fontSize: 13 }}>Loading…</div>
        ) : year4Students.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '36px 20px', color: '#94a3b8', fontSize: 13 }}>
            No 4th-year students found eligible for internship promotion.
            <div style={{ fontSize: 11, marginTop: 6 }}>
              All Year-4 students may already be interns, or none are enrolled yet.
            </div>
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 520 }}>
                <thead style={{ background: '#f8fafc' }}>
                  <tr>
                    <th style={{ padding: '9px 14px', textAlign: 'center', width: 44 }}>
                      <input
                        type="checkbox"
                        checked={selected.size === year4Students.length && year4Students.length > 0}
                        onChange={toggleAll}
                        style={{ accentColor: '#7c3aed', width: 15, height: 15, cursor: 'pointer' }}
                      />
                    </th>
                    {['Student ID', 'Name', 'Email', 'Enrollment Year', 'Departments'].map(h => (
                      <th key={h} style={{ padding: '9px 14px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.04em', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {year4Students.map((s, i) => {
                    const isSel = selected.has(s.profile_id);
                    return (
                      <tr
                        key={s.id}
                        style={{ background: isSel ? '#faf5ff' : i % 2 === 0 ? '#fff' : '#fafafa', borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}
                        onClick={() => toggleSelect(s.profile_id)}
                      >
                        <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={isSel}
                            onChange={() => {}}
                            style={{ accentColor: '#7c3aed', width: 15, height: 15, cursor: 'pointer' }}
                          />
                        </td>
                        <td style={{ padding: '10px 14px', fontFamily: 'monospace', fontSize: 12, color: '#64748b' }}>{s.student_id || '—'}</td>
                        <td style={{ padding: '10px 14px', fontWeight: 700, fontSize: 13, color: '#0f172a' }}>{s.full_name}</td>
                        <td style={{ padding: '10px 14px', fontSize: 12, color: '#64748b' }}>{s.email}</td>
                        <td style={{ padding: '10px 14px', fontSize: 12, color: '#64748b' }}>{s.enrollment_year || '—'}</td>
                        <td style={{ padding: '10px 14px', fontSize: 12 }}>
                          {s.mbbs_departments?.map(d => (
                            <span key={d.code} style={{ display: 'inline-block', padding: '2px 7px', borderRadius: 20, background: '#ede9fe', color: '#7c3aed', fontSize: 11, fontWeight: 600, marginRight: 4 }}>
                              {d.code}
                            </span>
                          ))}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Promotion controls */}
            <div style={{ padding: '14px 18px', borderTop: '1px solid #f1f5f9', background: '#fafafa', display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 260px' }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 5 }}>
                  Batch Notes (optional)
                </label>
                <input
                  type="text"
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  placeholder={`e.g. Batch ${new Date().getFullYear()} · Clinical Internship`}
                  style={{ width: '100%', boxSizing: 'border-box', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', outline: 'none', background: '#fff', color: '#0f172a' }}
                />
              </div>
              <button
                onClick={handlePromote}
                disabled={promoting || selected.size === 0}
                style={{ padding: '9px 22px', borderRadius: 9, border: 'none', background: selected.size === 0 ? '#e5e7eb' : 'linear-gradient(135deg,#7c3aed,#6d28d9)', color: selected.size === 0 ? '#94a3b8' : '#fff', fontSize: 14, fontWeight: 700, cursor: selected.size === 0 ? 'not-allowed' : 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}
              >
                {promoting ? 'Promoting…' : `🩺 Promote ${selected.size > 0 ? selected.size : ''} to Intern`}
              </button>
            </div>
          </>
        )}
      </div>

      {/* ── Existing Interns ── */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, overflow: 'hidden' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 800, color: '#0f172a' }}>Current Interns</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{existingInterns.length} record{existingInterns.length !== 1 ? 's' : ''}</div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {['ACTIVE', 'COMPLETED'].map(st => (
              <button
                key={st}
                onClick={() => setInternFilter(st)}
                style={{ padding: '5px 12px', borderRadius: 7, border: `1.5px solid ${internFilter === st ? (st === 'ACTIVE' ? '#1d4ed8' : '#166534') : '#e2e8f0'}`, background: internFilter === st ? (st === 'ACTIVE' ? '#dbeafe' : '#dcfce7') : '#fff', color: internFilter === st ? (st === 'ACTIVE' ? '#1d4ed8' : '#166534') : '#64748b', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
              >
                {st}
              </button>
            ))}
          </div>
        </div>
        {existingInterns.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '30px 20px', color: '#94a3b8', fontSize: 13 }}>
            No {internFilter.toLowerCase()} interns found.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 580 }}>
              <thead style={{ background: '#f8fafc' }}>
                <tr>
                  {['Name', 'Student ID', 'Stipend/mo', 'Start Date', 'Expected End', 'Status', 'Promoted By'].map(h => (
                    <th key={h} style={{ padding: '9px 14px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.04em', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {existingInterns.map((intern, i) => (
                  <tr key={intern.id} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa', borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '10px 14px', fontWeight: 700, fontSize: 13, color: '#0f172a' }}>{intern.user_name}</td>
                    <td style={{ padding: '10px 14px', fontFamily: 'monospace', fontSize: 12, color: '#64748b' }}>{intern.student_id || '—'}</td>
                    <td style={{ padding: '10px 14px', fontWeight: 700, color: '#166534', fontFamily: 'monospace' }}>₹{Number(intern.monthly_salary).toLocaleString('en-IN')}</td>
                    <td style={{ padding: '10px 14px', fontSize: 12, color: '#475569' }}>{fmtDate(intern.start_date)}</td>
                    <td style={{ padding: '10px 14px', fontSize: 12, color: '#475569' }}>{fmtDate(intern.expected_end_date)}</td>
                    <td style={{ padding: '10px 14px' }}>
                      <span style={{ padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: intern.status === 'ACTIVE' ? '#dbeafe' : '#dcfce7', color: intern.status === 'ACTIVE' ? '#1d4ed8' : '#166534' }}>
                        {intern.status_display || intern.status}
                      </span>
                    </td>
                    <td style={{ padding: '10px 14px', fontSize: 12, color: '#64748b' }}>{intern.promoted_by_name || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── main component ─────────────────────────────────────────────────────── */

export default function PromoteStudents() {
  const [promoteSubTab, setPromoteSubTab] = useState('year'); // 'year' | 'intern'

  // ── Year-promotion state ──────────────────────────────────────────────────
  const [students,    setStudents]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [promoting,   setPromoting]   = useState(false);
  const [error,       setError]       = useState(null);
  const [result,      setResult]      = useState(null);
  const [selected,    setSelected]    = useState(new Set());
  const [yearFilter,  setYearFilter]  = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [yearMap,     setYearMap]     = useState([]);

  /* ── load ─────────────────────────────────────────────────────────────── */
  const load = useCallback(async () => {
    try {
      setLoading(true); setError(null);
      const [studentsData, yearMapData] = await Promise.all([
        mbbsAPI.getStudents(),
        mbbsAPI.getYearMap(),
      ]);
      const list = Array.isArray(studentsData) ? studentsData : (studentsData.results || []);
      setStudents(list);
      setYearMap(Array.isArray(yearMapData) ? yearMapData : (yearMapData.results || []));
      setSelected(new Set());
    } catch (e) {
      setError('Failed to load MBBS students. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  /* ── derived ─────────────────────────────────────────────────────────── */
  const displayed = useMemo(() => {
    let list = students;
    if (yearFilter !== 'all') {
      list = list.filter(s => s.mbbs_year === parseInt(yearFilter));
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(s =>
        s.full_name?.toLowerCase().includes(q) ||
        s.student_id?.toLowerCase().includes(q) ||
        s.email?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [students, yearFilter, searchQuery]);

  const promotableDisplayed = displayed.filter(s => s.can_promote);
  const selectedList        = displayed.filter(s => selected.has(s.profile_id));
  const promotableSelected  = selectedList.filter(s => s.can_promote);
  const maxYearSelected     = selectedList.filter(s => !s.can_promote);

  const yearStats = useMemo(() => {
    const stats = {};
    ALL_YEARS.forEach(y => { stats[y] = students.filter(s => s.mbbs_year === y).length; });
    return stats;
  }, [students]);

  /* ── selection handlers ──────────────────────────────────────────────── */
  const toggleOne = (profileId) =>
    setSelected(prev => {
      const n = new Set(prev);
      n.has(profileId) ? n.delete(profileId) : n.add(profileId);
      return n;
    });

  const selectAllVisible = () =>
    setSelected(prev => {
      const n = new Set(prev);
      promotableDisplayed.forEach(s => n.add(s.profile_id));
      return n;
    });

  const deselectAllVisible = () =>
    setSelected(prev => {
      const n = new Set(prev);
      displayed.forEach(s => n.delete(s.profile_id));
      return n;
    });

  const clearAll = () => setSelected(new Set());

  const allPromotableSelected =
    promotableDisplayed.length > 0 &&
    promotableDisplayed.every(s => selected.has(s.profile_id));

  /* ── promote ──────────────────────────────────────────────────────────── */
  const handlePromote = async () => {
    if (promotableSelected.length === 0) {
      notify.info('No promotable students selected. Year 4 students cannot be promoted further.');
      return;
    }

    const lines = [
      `Promote ${promotableSelected.length} student(s) to the next academic year?`,
      '',
      'This will:',
      '  • Increment their MBBS year by 1',
      '  • Update their assigned departments automatically',
      '',
    ];
    if (maxYearSelected.length > 0) {
      lines.push(`  ⚠ ${maxYearSelected.length} selected student(s) are already in Year 4 and will be skipped.`);
    }

    if (!await confirmDialog(lines.join('\n'))) return;

    try {
      setPromoting(true); setError(null);
      const profileIds = selectedList.map(s => s.profile_id);
      const res = await mbbsAPI.promoteStudents(profileIds);
      setResult(res);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || 'Promotion failed. Please try again.');
    } finally {
      setPromoting(false);
    }
  };

  /* ── render ───────────────────────────────────────────────────────────── */

  // Sub-tab switcher UI (shared across both tabs)
  const SubTabBar = () => (
    <div style={S.subTabBar}>
      <button
        onClick={() => setPromoteSubTab('year')}
        style={{
          ...S.subTabBtn,
          background:   promoteSubTab === 'year' ? '#4f46e5' : 'transparent',
          color:        promoteSubTab === 'year' ? '#fff'    : '#64748b',
          borderBottom: promoteSubTab === 'year' ? '2px solid #4f46e5' : '2px solid transparent',
        }}
      >
        🎓 Year Promotion
      </button>
      <button
        onClick={() => setPromoteSubTab('intern')}
        style={{
          ...S.subTabBtn,
          background:   promoteSubTab === 'intern' ? '#7c3aed' : 'transparent',
          color:        promoteSubTab === 'intern' ? '#fff'    : '#64748b',
          borderBottom: promoteSubTab === 'intern' ? '2px solid #7c3aed' : '2px solid transparent',
        }}
      >
        🩺 To Internship
      </button>
    </div>
  );

  if (loading && promoteSubTab === 'year') {
    return (
      <div style={S.wrap}>
        <SubTabBar />
        <div style={S.loadingWrap}>
          <div style={S.spinner} />
          <span style={{ color: '#64748b', fontSize: 14 }}>Loading MBBS students…</span>
        </div>
      </div>
    );
  }

  return (
    <div style={S.wrap}>
      {/* ── Sub-tab switcher ── */}
      <SubTabBar />

      {/* ── Intern Promotion Tab ── */}
      {promoteSubTab === 'intern' && (
        <InternPromotionPanel />
      )}

      {/* ── Year Promotion Tab ── */}
      {promoteSubTab === 'year' && (
        <>
          {error && (
            <div style={S.alertErr}>
              {error}
              <button style={S.alertClose} onClick={() => setError(null)}>×</button>
            </div>
          )}

          {/* ── Year stats bar ── */}
          <div style={S.statsBar}>
            {ALL_YEARS.map(y => {
              const cfg = YEAR_CONFIG[y];
              const count = yearStats[y] || 0;
              return (
                <div key={y} style={{ ...S.statCard, borderColor: cfg.color }}>
                  <span style={{ ...S.statNum, color: cfg.color }}>{count}</span>
                  <span style={S.statLabel}>{cfg.label}</span>
                  <span style={S.statSubjects}>{cfg.subjects.split(' · ')[0]}…</span>
                </div>
              );
            })}
            <div style={{ ...S.statCard, borderColor: '#475569' }}>
              <span style={{ ...S.statNum, color: '#475569' }}>{students.length}</span>
              <span style={S.statLabel}>Total MBBS</span>
              <span style={S.statSubjects}>All years</span>
            </div>
          </div>

          {/* ── Year map info panel ── */}
          {yearMap.length > 0 && (
            <details style={S.yearMapDetails}>
              <summary style={S.yearMapSummary}>📚 Year → Subject Mapping</summary>
              <div style={S.yearMapGrid}>
                {yearMap.map(m => (
                  <div key={m.year} style={S.yearMapCard}>
                    <span style={{ ...S.yearMapYearBadge, background: YEAR_CONFIG[m.year]?.bg, color: YEAR_CONFIG[m.year]?.color }}>
                      {m.year_label}
                    </span>
                    <div style={S.yearMapDepts}>
                      {m.departments.map(d => (
                        <span key={d.id} style={S.yearMapDeptChip}>{d.name}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </details>
          )}

          {/* ── Toolbar ── */}
          <div style={S.toolbar}>
            <div style={S.toolbarLeft}>
              {/* Year filter */}
              <div style={S.filterGroup}>
                {['all', '1', '2', '3', '4'].map(y => {
                  const isAll  = y === 'all';
                  const active = yearFilter === y;
                  const cfg    = isAll ? null : YEAR_CONFIG[parseInt(y)];
                  return (
                    <button
                      key={y}
                      onClick={() => { setYearFilter(y); setSelected(new Set()); }}
                      style={{
                        ...S.filterBtn,
                        background:  active ? (cfg?.color || '#334155') : '#f8fafc',
                        color:        active ? '#fff' : '#475569',
                        borderColor:  active ? (cfg?.color || '#334155') : '#e2e8f0',
                      }}
                    >
                      {isAll ? `All (${students.length})` : `Year ${y} (${yearStats[parseInt(y)] || 0})`}
                    </button>
                  );
                })}
              </div>

              {/* Search */}
              <div style={S.searchWrap}>
                <span style={S.searchIcon}>🔍</span>
                <input
                  type="search"
                  placeholder="Search by name, ID, email…"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  style={S.searchInput}
                />
              </div>
            </div>

            <div style={S.toolbarRight}>
              {/* Select/deselect controls */}
              {displayed.length > 0 && (
                <>
                  {!allPromotableSelected ? (
                    <button style={S.selectBtn} onClick={selectAllVisible}>
                      ☐ Select Promotable ({promotableDisplayed.length})
                    </button>
                  ) : (
                    <button style={S.selectBtn} onClick={deselectAllVisible}>
                      ☑ Deselect All
                    </button>
                  )}
                  {selected.size > 0 && (
                    <button style={S.clearBtn} onClick={clearAll}>Clear All</button>
                  )}
                </>
              )}

              {/* Promote button */}
              <button
                style={{
                  ...S.promoteBtn,
                  opacity: promotableSelected.length === 0 || promoting ? 0.6 : 1,
                  cursor:  promotableSelected.length === 0 || promoting ? 'not-allowed' : 'pointer',
                }}
                onClick={handlePromote}
                disabled={promotableSelected.length === 0 || promoting}
              >
                {promoting
                  ? '⏳ Promoting…'
                  : promotableSelected.length > 0
                    ? `🎓 Promote ${promotableSelected.length} Student${promotableSelected.length !== 1 ? 's' : ''}`
                    : '🎓 Promote Selected'
                }
              </button>
            </div>
          </div>

          {/* Selection summary bar */}
          {selected.size > 0 && (
            <div style={S.selectionBar}>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>
                {selected.size} selected
              </span>
              {promotableSelected.length > 0 && (
                <span style={S.selBarChip}>✓ {promotableSelected.length} will be promoted</span>
              )}
              {maxYearSelected.length > 0 && (
                <span style={{ ...S.selBarChip, background: '#fef9c3', color: '#854d0e' }}>
                  ⚠ {maxYearSelected.length} at max year (will be skipped)
                </span>
              )}
            </div>
          )}

          {/* ── Student table ── */}
          {displayed.length === 0 ? (
            <div style={S.empty}>
              {students.length === 0
                ? 'No MBBS students found. Register MBBS students to get started.'
                : 'No students match the current filters.'}
            </div>
          ) : (
            <div style={S.tableWrap}>
              <table style={S.table}>
                <thead>
                  <tr style={S.thead}>
                    <th style={S.th}>
                      <input
                        type="checkbox"
                        checked={allPromotableSelected && promotableDisplayed.length > 0}
                        onChange={allPromotableSelected ? deselectAllVisible : selectAllVisible}
                        style={{ cursor: 'pointer', width: 15, height: 15, accentColor: '#4f46e5' }}
                        title={allPromotableSelected ? 'Deselect all' : 'Select all promotable'}
                      />
                    </th>
                    <th style={S.th}>Student</th>
                    <th style={S.th}>ID</th>
                    <th style={S.th}>Current Year</th>
                    <th style={S.th}>Subjects / Departments</th>
                    <th style={S.th}>Next Year</th>
                    <th style={S.th}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {displayed.map((student, idx) => {
                    const isSelected = selected.has(student.profile_id);
                    const canPromote = student.can_promote;
                    const nextYear   = (student.mbbs_year || 1) + 1;

                    return (
                      <tr
                        key={student.profile_id}
                        style={{
                          ...S.tr,
                          background: isSelected ? '#f0f4ff' : idx % 2 === 0 ? '#fff' : '#fafafa',
                          borderLeft: isSelected ? '3px solid #4f46e5' : '3px solid transparent',
                        }}
                        onClick={() => canPromote && toggleOne(student.profile_id)}
                      >
                        {/* Checkbox */}
                        <td style={S.td}>
                          {canPromote ? (
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleOne(student.profile_id)}
                              onClick={e => e.stopPropagation()}
                              style={{ cursor: 'pointer', width: 15, height: 15, accentColor: '#4f46e5' }}
                            />
                          ) : (
                            <span title="Cannot promote — max year reached"
                              style={{ color: '#d1d5db', fontSize: 16 }}>⊘</span>
                          )}
                        </td>

                        {/* Student name */}
                        <td style={S.td}>
                          <div style={{ fontWeight: 600, color: '#0f172a', fontSize: 14 }}>
                            {student.full_name}
                          </div>
                          <div style={{ fontSize: 11, color: '#94a3b8' }}>{student.email}</div>
                        </td>

                        {/* Student ID */}
                        <td style={S.td}>
                          <code style={S.idCode}>{student.student_id || '—'}</code>
                        </td>

                        {/* Current year */}
                        <td style={S.td}>
                          <YearBadge year={student.mbbs_year || 1} />
                        </td>

                        {/* Departments */}
                        <td style={{ ...S.td, maxWidth: 260 }}>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            {(student.mbbs_departments || []).map(d => (
                              <DeptChip key={d.id} dept={d} />
                            ))}
                            {(!student.mbbs_departments || student.mbbs_departments.length === 0) && (
                              <span style={{ color: '#94a3b8', fontSize: 12 }}>No departments assigned</span>
                            )}
                          </div>
                        </td>

                        {/* Next year preview */}
                        <td style={S.td}>
                          {canPromote ? (
                            <YearBadge year={nextYear} size="sm" />
                          ) : (
                            <span style={{ fontSize: 12, color: '#7c3aed', fontWeight: 600 }}>
                              ✓ Final Year
                            </span>
                          )}
                        </td>

                        {/* Status */}
                        <td style={S.td}>
                          {canPromote ? (
                            <span style={{ ...S.statusBadge, background: '#dcfce7', color: '#166534' }}>
                              Promotable
                            </span>
                          ) : (
                            <span style={{ ...S.statusBadge, background: '#ede9fe', color: '#6d28d9' }}>
                              Max Year
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Footer count */}
          {displayed.length > 0 && (
            <div style={S.footer}>
              Showing {displayed.length} of {students.length} MBBS students
              {yearFilter !== 'all' && ` · Year ${yearFilter} filter active`}
              {searchQuery && ` · Search: "${searchQuery}"`}
            </div>
          )}

          {/* Result modal */}
          <ResultModal result={result} onClose={() => setResult(null)} />
        </>
      )}
    </div>
  );
}

/* ─── styles ─────────────────────────────────────────────────────────────── */
const S = {
  wrap: { fontFamily: "'DM Sans','Segoe UI',sans-serif", padding: 0 },

  /* sub-tab bar */
  subTabBar: { display: 'flex', gap: 0, marginBottom: 20, borderBottom: '1px solid #e5e7eb', background: '#f8fafc', borderRadius: '10px 10px 0 0', overflow: 'hidden' },
  subTabBtn: { flex: 1, padding: '11px 18px', border: 'none', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', transition: 'all .15s', borderRadius: 0 },

  loadingWrap: { display: 'flex', alignItems: 'center', gap: 12, padding: 60, justifyContent: 'center' },
  spinner: { width: 22, height: 22, border: '3px solid #e2e8f0', borderTopColor: '#4f46e5', borderRadius: '50%', animation: 'spin .7s linear infinite', flexShrink: 0 },

  alertErr:   { background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 13, position: 'relative' },
  alertClose: { position: 'absolute', top: 8, right: 10, background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#94a3b8' },

  /* stats bar */
  statsBar:    { display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' },
  statCard:    { flex: '1 1 130px', border: '2px solid', borderRadius: 12, padding: '12px 16px', background: '#fff', display: 'flex', flexDirection: 'column', gap: 2 },
  statNum:     { fontSize: 28, fontWeight: 800, lineHeight: 1 },
  statLabel:   { fontSize: 13, fontWeight: 700, color: '#334155' },
  statSubjects:{ fontSize: 11, color: '#94a3b8' },

  /* year map details */
  yearMapDetails: { marginBottom: 20, border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' },
  yearMapSummary: { padding: '10px 14px', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#374151', background: '#f8fafc', userSelect: 'none' },
  yearMapGrid:    { display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(220px,1fr))', gap: 12, padding: 14 },
  yearMapCard:    { border: '1px solid #e2e8f0', borderRadius: 8, padding: 12, background: '#fff' },
  yearMapYearBadge: { display: 'inline-block', padding: '3px 10px', borderRadius: 20, fontWeight: 700, fontSize: 12, marginBottom: 8 },
  yearMapDepts:   { display: 'flex', flexDirection: 'column', gap: 4 },
  yearMapDeptChip:{ fontSize: 12, color: '#475569' },

  /* toolbar */
  toolbar:     { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12, gap: 12, flexWrap: 'wrap' },
  toolbarLeft: { display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', flex: 1 },
  toolbarRight:{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' },

  filterGroup: { display: 'flex', gap: 4, flexWrap: 'wrap' },
  filterBtn:   { padding: '6px 12px', borderRadius: 8, border: '1px solid', fontSize: 12, fontWeight: 600, cursor: 'pointer', transition: 'all .1s', whiteSpace: 'nowrap' },

  searchWrap:  { position: 'relative', flex: '1 1 200px', maxWidth: 280 },
  searchIcon:  { position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 13, pointerEvents: 'none' },
  searchInput: { padding: '7px 10px 7px 30px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, width: '100%', boxSizing: 'border-box', outline: 'none' },

  selectBtn:  { padding: '7px 13px', border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' },
  clearBtn:   { padding: '7px 12px', border: '1px solid #fecaca', borderRadius: 8, background: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#ef4444' },
  promoteBtn: { padding: '9px 20px', borderRadius: 9, border: 'none', background: 'linear-gradient(135deg,#4f46e5,#7c3aed)', color: '#fff', fontSize: 14, fontWeight: 700, transition: 'opacity .15s', whiteSpace: 'nowrap' },

  /* selection bar */
  selectionBar: { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px', background: '#f0f4ff', border: '1px solid #c7d2fe', borderRadius: 8, marginBottom: 10, flexWrap: 'wrap' },
  selBarChip:   { padding: '2px 10px', borderRadius: 20, background: '#dcfce7', color: '#166534', fontSize: 12, fontWeight: 600 },

  /* table */
  tableWrap: { border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' },
  table:     { width: '100%', borderCollapse: 'collapse' },
  thead:     { background: '#f8fafc' },
  th:        { padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.04em', borderBottom: '2px solid #e5e7eb', whiteSpace: 'nowrap' },
  tr:        { transition: 'background .1s', cursor: 'pointer' },
  td:        { padding: '12px 14px', borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle', fontSize: 13 },

  idCode:      { background: '#f1f5f9', color: '#475569', padding: '2px 6px', borderRadius: 5, fontSize: 12, fontFamily: 'monospace' },
  statusBadge: { display: 'inline-block', padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600 },

  /* footer */
  footer: { fontSize: 12, color: '#94a3b8', marginTop: 10, textAlign: 'right' },

  /* empty */
  empty: { textAlign: 'center', padding: '60px 24px', color: '#94a3b8', fontSize: 14, background: '#f8fafc', borderRadius: 12, border: '2px dashed #e2e8f0' },

  /* result modal */
  overlay:      { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 16 },
  resultModal:  { background: '#fff', borderRadius: 16, width: '100%', maxWidth: 540, maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 24px 60px #0005' },
  resultHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 20px', borderBottom: '1px solid #f1f5f9' },
  resultTitle:  { fontSize: 18, fontWeight: 700, color: '#0f172a', margin: 0 },
  closeBtn:     { background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: '#94a3b8', lineHeight: 1, padding: 0 },
  resultSummary:{ display: 'flex', gap: 12, padding: '16px 20px', flexWrap: 'wrap' },
  resultChip:   { flex: '1 1 100px', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '12px 16px', borderRadius: 10 },
  resultChipNum:  { fontSize: 28, fontWeight: 800, lineHeight: 1 },
  resultChipLabel:{ fontSize: 12, fontWeight: 600, marginTop: 4 },
  resultSection:      { padding: '12px 20px', borderTop: '1px solid #f1f5f9' },
  resultSectionTitle: { fontSize: 13, fontWeight: 700, color: '#166534', marginBottom: 10 },
  resultList: { display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 200, overflowY: 'auto' },
  resultRow:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13, padding: '6px 10px', background: '#f8fafc', borderRadius: 7 },
  resultName: { fontWeight: 600, color: '#0f172a', flex: 1 },
  doneBtn:    { display: 'block', margin: '16px 20px', padding: '10px 24px', borderRadius: 8, border: 'none', background: '#4f46e5', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer', width: 'calc(100% - 40px)' },
};

/* inject keyframe once */
if (typeof document !== 'undefined' && !document.getElementById('ps-spin-kf')) {
  const style = document.createElement('style');
  style.id = 'ps-spin-kf';
  style.textContent = '@keyframes spin{to{transform:rotate(360deg)}}';
  document.head.appendChild(style);
}